import React, { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';

// Import your custom function to get the access token
import { getAccessToken } from '../data/index.js';
import { Stack } from '@mui/system';
import { Typography } from '@mui/material';

// --- Helper Functions ---

/**
 * Generates a consistent color from a string (e.g., a user's name).
 * @param {string} name The input string.
 * @returns {string} A hex color code.
 */
function stringToColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

/**
 * Creates initials from a display name.
 * @param {string} name The user's full display name.
 * @returns {string} The initials.
 */
function getInitials(name = '') {
  if (!name) return '?'
  const parts = name.split(' ').filter(part => !part.startsWith('['))
  if (parts.length < 2) return ''
  const firstNameInitial = parts[0][0]
  const lastNameInitial = parts[parts.length - 1][0]
  return `${firstNameInitial}${lastNameInitial}`.toUpperCase()
}


// --- Main Component ---

/**
 * Displays a user's contact card with an avatar that falls back to initials.
 * Fetches data from Microsoft Graph API using a provided access token.
 * @param {{authorEmail: string, authorName: string, authorJobTitle: string}} props
 */
function ArticleAuthorProfile({ authorEmail, authorName, authorJobTitle }) {
  const [userData, setUserData] = useState(null);
  const [avatarSrc, setAvatarSrc] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Reset state when the email prop changes
    setUserData(null);
    setAvatarSrc(null);
    setError(null);
    setIsLoading(true);

    const fetchGraphData = async () => {
      if (!authorEmail) {
        setError('No user email provided.');
        setIsLoading(false);
        return;
      }

      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error('Could not retrieve access token.');
        }

        const headers = { Authorization: `Bearer ${accessToken}` };
        
        // Define endpoints
        const dataEndpoint = `https://graph.microsoft.com/v1.0/users/${authorEmail}?$select=displayName,jobTitle,mail,department`;
        const photoEndpoint = `https://graph.microsoft.com/v1.0/users/${authorEmail}/photo/$value`;

        // Fetch user data and photo in parallel for efficiency
        const [dataResponse, photoResponse] = await Promise.all([
            fetch(dataEndpoint, { headers }),
            fetch(photoEndpoint, { headers })
        ]);

        // Process user data
        if (!dataResponse.ok) {
            throw new Error(`Failed to fetch user data: ${dataResponse.statusText}`);
        }
        const fetchedUserData = await dataResponse.json();
        setUserData(fetchedUserData);

        // Process user photo (a 404 is an expected, non-critical error)
        if (photoResponse.ok) {
          const imageBlob = await photoResponse.blob();
          setAvatarSrc(URL.createObjectURL(imageBlob));
        }

      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGraphData();
  }, [authorEmail]);

  if (isLoading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', fontFamily: 'sans-serif', gap: '16px' }}>
      <Avatar
        src={avatarSrc}
        sx={{
          width: 48,
          height: 48,
          fontSize: '1.75rem',
          bgcolor: stringToColor(userData?.displayName ?? authorName),
        }}
      >
        {getInitials(userData?.displayName ?? authorName)}
      </Avatar>
      <Stack rowGap={.5} padding='1%'>
        <Typography variant="body">
          {userData?.displayName ?? authorName}
        </Typography>
        { (userData?.jobTitle || authorJobTitle) && 
          <Typography variant="caption" fontStyle='text.secondary'>
            { userData?.jobTitle ?? authorJobTitle }
          </Typography>
        }
      </Stack>
    </div>
  );
}

export default ArticleAuthorProfile;